import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { RootState } from '../store';
import { BehaviorSubject, combineLatest, firstValueFrom } from 'rxjs';
import { LocalOfficesRestService } from '../services/rest/local-offices.rest.service';
import { BusyAction, BusyDoneAction } from '../store/ui/ui.actions';
import { LocalOffice } from '../store/admin/local-office/local-office.interface';
import { distinctUntilChanged } from 'rxjs/operators';

const PROCESSING_LOCAL_OFFICES_BUSY_KEY = 'processing-local-offices';

@Injectable({providedIn: "root"})
export class LocalOfficeSandbox {

  editableLocalOffices$ = new BehaviorSubject<LocalOffice[]>([]);
  loading$ = new BehaviorSubject<boolean>(false);
  saving$ = new BehaviorSubject<boolean>(false);
  processing$ = combineLatest([this.loading$, this.saving$], (loading, saving) => loading || saving);

  constructor(private store: Store<RootState>, private restService: LocalOfficesRestService){
    this.processing$.pipe(distinctUntilChanged()).subscribe(processing => {
      if (processing) {
        this.store.dispatch(new BusyAction(PROCESSING_LOCAL_OFFICES_BUSY_KEY));
      } else {
        this.store.dispatch(new BusyDoneAction(PROCESSING_LOCAL_OFFICES_BUSY_KEY));
      }
    })
  }

  async load(): Promise<void> {
    this.loading$.next(true);
    try {
      this.editableLocalOffices$.next([]);
      const salesOrganisations = await firstValueFrom(this.store.select(state => state.session.userContext.salesOrganisations));
      const localOffices = await firstValueFrom(this.restService.getAll());
      const filteredLocalOffices = salesOrganisations
        .map(salesOrganisation => localOffices.find(localOffice => localOffice.id === salesOrganisation.id))
        .filter(localOffice => !!localOffice);
      this.editableLocalOffices$.next(filteredLocalOffices);
    } finally {
      this.loading$.next(false);
    }
  }

  async save(localOffice: LocalOffice): Promise<void> {
    this.saving$.next(true);
    try {
      await firstValueFrom(this.restService.save(localOffice));
      await this.load();
    } finally {
      this.saving$.next(false);
    }
  }
}
